/**
 * Lightweight horizontal grouped bar chart in pure SVG.
 *
 * Props:
 *   metric: { key, label, unit, lowerBetter? }
 *   athletes: [{ id, name, color, value }]
 *   reference?: { label, value, color }   — peer/position avg line
 *   height?: number (px per bar, default 22)
 *   minWidth?: number (chart width)
 */
export default function BarChart({ metric, athletes, reference, height = 22, minWidth = 320 }) {
  const padding = 8;
  const labelWidth = 96;
  const numericWidth = 60;
  const dotColumn = 12;
  const barAreaWidth = minWidth - labelWidth - numericWidth - padding * 2 - dotColumn;

  const values = athletes.map(a => a.value).filter(v => v != null);
  if (values.length === 0) {
    return <p className="text-xs text-zinc-400 py-3 text-center">No data for {metric.label}</p>;
  }
  const refVal = reference?.value;
  const all = [...values, ...(refVal != null ? [refVal] : [])];
  const maxV = Math.max(...all) * 1.05;
  const minV = 0;
  const range = maxV - minV || 1;

  const totalHeight = athletes.length * (height + 6) + padding * 2;
  // Bars start after label + dot column
  const barStartX = padding + labelWidth + dotColumn;
  const refX = refVal != null ? barStartX + (refVal - minV) / range * barAreaWidth : null;

  return (
    <div className="w-full overflow-x-auto">
      <svg width="100%" viewBox={`0 0 ${minWidth} ${totalHeight}`} role="img" aria-label={`${metric.label} comparison`}>
        {/* Reference line (peer avg) */}
        {refX != null && (
          <g>
            <line x1={refX} x2={refX} y1={padding} y2={totalHeight - padding} stroke={reference.color || "#94a3b8"} strokeDasharray="3,3" strokeWidth={1.2} />
            <text x={refX} y={padding} fontSize={8} fill={reference.color || "#94a3b8"} textAnchor="middle" dy={-2}>
              {reference.label}: {refVal}{metric.unit}
            </text>
          </g>
        )}
        {athletes.map((a, i) => {
          const y = padding + i * (height + 6);
          const v = a.value;
          const w = v != null ? Math.max(2, (v - minV) / range * barAreaWidth) : 0;
          const isBest = v != null && (metric.lowerBetter ? v === Math.min(...values) : v === Math.max(...values)) && values.length > 1;
          return (
            <g key={a.id}>
              {/* Name aligned to the very left, textAnchor start */}
              <text x={padding} y={y + height / 2} fontSize={10} fill="#3f3f46" textAnchor="start" dominantBaseline="middle" fontWeight={isBest ? 600 : 400}>
                {a.name.length > 14 ? a.name.slice(0, 13) + "…" : a.name}
              </text>
              {/* Dot moved to dedicated column between name and bar */}
              {isBest && (
                <circle cx={padding + labelWidth + dotColumn / 2} cy={y + height / 2} r={3} fill="#F85037" />
              )}
              <rect x={barStartX} y={y} width={barAreaWidth} height={height} fill="#f4f4f5" rx={3} />
              <rect x={barStartX} y={y} width={w} height={height} fill={a.color} rx={3} opacity={v != null ? 1 : 0.3} />
              <text x={barStartX + w + 4} y={y + height / 2} fontSize={10} fill="#27272a" dominantBaseline="middle" fontWeight={isBest ? 700 : 500}>
                {v != null ? `${v}${metric.unit}` : "—"}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
